/**
 * Content-Design-authored copy and validation-message formatters for the
 * exposure section pages (TG-6, AC-15). EXPOSURE-QUESTIONS.md's own
 * "Validation" section states the error-summary convention verbatim: "one
 * link per problem ... in the H2 wording (for example 'Question 2: enter
 * details')" — these are content FUNCTIONS (like
 * `renewal-capture/content.ts`'s own `progressLabel` and
 * `content/declaration-submission.ts`'s `sectionsNotCompleteMessage`), not
 * a single fixed string, because the question number and the specific
 * missing field are inherently per-call.
 */
export const exposureContent = {
  yesLabel: "Yes",
  noLabel: "No",
  addAnotherChangeLabel: "Add another change",
  removeLabel: "Remove",
  errorSummaryTitle: "There is a problem",

  // ADR-20261102-h2fd: H2's own wording (04-05-GuidedExposures.dc.html's validation): the summary
  // names the question, the message under the field states what to do.
  questionMissingAnswer(number: string): string {
    return `Question ${number}: select yes or no`;
  },
  fieldMissingAnswer: "Select yes or no",
  fieldMissingDetail: "Enter details",
  questionMissingFigure(number: string): string {
    return `Question ${number}: enter a figure`;
  },
  fieldMissingFigure: "Enter a figure",
  questionMissingDetail(number: string): string {
    return `Question ${number}: enter details`;
  },
  questionMissingRepeatItem(number: string): string {
    return `Question ${number}: add at least one item`;
  },

  // H2 04-05-GuidedExposures `errorsFor`: a repeat row's messages name the row ("for vehicle 1"),
  // except in a single-row block.
  repeatRowSuffix(itemLabel: string, rowNumber: number, single: boolean): string {
    return single ? "" : ` for ${itemLabel.toLowerCase()} ${rowNumber}`;
  },
  questionSelectMissing(number: string, emptyLabel: string, where: string): string {
    return `Question ${number}: ${emptyLabel.toLowerCase()}${where}`;
  },
  defaultSelectEmptyLabel: "Select an option",
  questionRadioMissing(number: string, label: string, where: string): string {
    return `Question ${number}: answer \u201c${label}\u201d${where}`;
  },
  fieldRadioMissing: "Select an answer",
  questionFieldMissing(number: string, fieldName: string, where: string): string {
    return `Question ${number}: enter ${fieldName.toLowerCase()}${where}`;
  },
  fieldFieldMissing(fieldName: string): string {
    return `Enter ${fieldName.toLowerCase()}`;
  },
  questionYearDigits(number: string, fieldName: string, where: string): string {
    return `Question ${number}: ${fieldName.toLowerCase()} must include 4 numbers${where}`;
  },
  fieldYearDigits(fieldName: string): string {
    return `${fieldName} must include 4 numbers`;
  },
  questionInPast(number: string, fieldName: string, where: string): string {
    return `Question ${number}: ${fieldName.toLowerCase()} must be in the past${where}`;
  },
  fieldInPast(fieldName: string): string {
    return `${fieldName} must be in the past`;
  },
  questionRealDate(number: string, fieldName: string, where: string): string {
    return `Question ${number}: enter ${fieldName.toLowerCase()} as a real date${where}`;
  },
  fieldRealDate(fieldName: string): string {
    return `Enter ${fieldName.toLowerCase()} as a real date`;
  },
  questionNoSystem(number: string, where: string): string {
    return `Question ${number}: select at least one system${where}`;
  },
  fieldNoSystem: "Select at least one system",
  questionSystemYearMissing(number: string, system: string, where: string): string {
    return `Question ${number}: enter the year the ${system} improvement was made${where}`;
  },
  fieldSystemYearMissing: "Enter the year",
  questionSystemYearDigits(number: string, system: string, where: string): string {
    return `Question ${number}: the year the ${system} improvement was made must include 4 numbers${where}`;
  },
  fieldSystemYearDigits: "Year improvement was made must include 4 numbers",
  questionSystemYearFuture(number: string, system: string, where: string): string {
    return `Question ${number}: the year the ${system} improvement was made must be in the past${where}`;
  },
  fieldSystemYearFuture: "Year improvement was made must be in the past",
  systemYearLabel: "Year improvement was made",
  dateFormatHelp: "Use the format MM / DD / YYYY",

  // H2 commercial property question 4 (`properties`/`limits`).
  limitsPropertyLabel: "Which property?",
  limitsPropertyEmptyLabel: "Select a property",
  limitsPropertyNoneLeftLabel: "No properties left to select",
  limitsRemovePropertyLabel: "Remove property",
  limitsAddPropertyLabel: "Add another property",
  limitsSubjectLabel: "Subject of insurance",
  limitsAddSubjectLabel: "Add",
  limitsAllSubjectsAdded: "All subjects of insurance have been added.",
  limitsSubjectNotChosen: "Select a subject of insurance.",
  questionLimitsProperty(number: string): string {
    return `Question ${number}: select a property`;
  },
  fieldLimitsProperty: "Select a property",
  questionLimitsNoSubject(number: string): string {
    return `Question ${number}: add at least one subject of insurance`;
  },
  fieldLimitsNoSubject: "Select a subject of insurance and add it",
  questionLimitsValue(number: string, subject: string): string {
    return `Question ${number}: enter a limit for ${subject}`;
  },
  fieldLimitsValue: "Enter a limit",
  limitsValueLabel: "Updated limit",
  limitsCurrentTriggerLabel(subject: string): string {
    return `Current limit on file for ${subject}`;
  },
  limitsCurrentText(amount: string): string {
    return `Current limit on file: ${amount}. We do not have its valuation basis on file yet.`;
  },

  // H2 `current`: the figure on file behind a help trigger after a field label.
  currentFigureTriggerLabel(fieldLabel: string): string {
    return `Current figure on file for ${fieldLabel}`;
  },
  currentFigureText(value: string): string {
    return `Current figure on file: ${value}`;
  },
  pickedFigureTriggerLabel(fieldLabel: string): string {
    return `Current ${fieldLabel.toLowerCase()} on file for ${fieldLabel}`;
  },
  pickedFigureText(fieldLabel: string, value: string): string {
    return `Current ${fieldLabel.toLowerCase()} on file: ${value}`;
  },

  // H2 additional-details pages: the section help beside the title, and each row's title.
  detailsHelpTriggerLabel: "Why are we asking for this?",
  detailsNewItemTitle(noun: string, ordinal: number, replacing: string): string {
    return replacing === "" ? `New ${noun} ${ordinal}` : `New ${noun} ${ordinal} \u2014 replacing ${replacing}`;
  },
  rowTitle(itemLabel: string, rowNumber: number, noTitle: boolean): string {
    return noTitle ? `Change ${rowNumber}` : `${itemLabel} ${rowNumber}`;
  },

  // TG-5 (m1-exposure-detail-sourcing, Content Design Agent, AC-3/AC-4):
  // the three data-on-file wordings — nothing-on-file (ADR "Option A":
  // empty, answerable, no placeholder), failed-read (built on the
  // existing `content.ts` `serverErrorMessage` pattern, distinct from
  // nothing-on-file per the architecture plan) and picker no-items
  // (H2 EXPOSURE-QUESTIONS.md's own "No … left to select" convention).
  onFileNothingOnFile(itemsLabel: string): string {
    return `We do not have any ${itemsLabel} on file for this policy yet.`;
  },
  onFileReadFailedMessage: "We could not load what we hold on file. Try again.",
  onFileRetryLabel: "Try again",
  onFileNoneLeftToSelect(itemsLabel: string): string {
    return `No ${itemsLabel} left to select`;
  },
  onFileHelpTriggerLabel(itemsLabel: string): string {
    return `Which ${itemsLabel} do we hold on file?`;
  },
  onFileGeneralLiabilityHelpTriggerLabel: "What classifications and estimates do we hold on file?",
  // The label for an on-file item whose label attributes are all blank --
  // its 1-based position in the on-file list, shown in both the panel's
  // item list and the picker's options so the two agree.
  onFileItemFallbackLabel(itemNumber: number): string {
    return `Item ${itemNumber}`;
  },

  // EXPOSURE-QUESTIONS.md §14 / H2 `04-05-GuidedExposures.dc.html`'s own
  // `defLabel: 'What is ' + label + '?'` -- the checklist's per-item
  // definition disclosure trigger's accessible name.
  /** H2 04-05-GuidedExposures, additional coverage considerations: the field "Other" reveals. */
  otherCoverageLabel: "Tell us which coverage you would like information about",
  checklistDefinitionTriggerLabel(label: string): string {
    return `What is ${label}?`;
  },
  soldReplacedRemovedFlag(word: string): string {
    return `— ${word}`;
  },
} as const;
